import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Save, CheckCircle, PenTool, AlertTriangle } from 'lucide-react'
import FormRenderer from '../../components/schede/renderer/FormRenderer'
import FirmaSempliceModal from '../../components/schede/FirmaSempliceModal'
import api from '../../core/services/api'

interface SchedaPubblicaData {
  codice: string
  template_nome: string
  template_descrizione: string
  entity_label: string
  schema: any[]
  dati: Record<string, any>
  stato: string
  is_readonly: boolean
  tenant_name: string
}

export default function SchedaPubblicaPage() {
  const { tenantCode, token } = useParams<{ tenantCode: string; token: string }>()
  const [scheda, setScheda] = useState<SchedaPubblicaData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dati, setDati] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showFirma, setShowFirma] = useState(false)
  const [firmaLoading, setFirmaLoading] = useState(false)

  useEffect(() => {
    loadScheda()
  }, [tenantCode, token])

  const loadScheda = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/schede-pubbliche/compila/${tenantCode}/${token}`)
      setScheda(res.data)
      setDati(res.data.dati || {})
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 410) {
        setError('Questo link e\' scaduto. Contatta chi te lo ha inviato per richiederne uno nuovo.')
      } else if (status === 404) {
        setError('Link non valido. Verifica di aver copiato correttamente l\'indirizzo.')
      } else {
        setError('Errore nel caricamento della scheda.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!scheda) return
    setSaving(true)
    try {
      await api.put(`/v1/schede-pubbliche/compila/${tenantCode}/${token}`, { dati })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      await loadScheda()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleFirma = async (dataUrl: string, nominativo: string) => {
    if (!scheda) return
    setFirmaLoading(true)
    try {
      await api.put(`/v1/schede-pubbliche/compila/${tenantCode}/${token}`, {
        dati,
        firma_semplice: dataUrl,
        firma_nominativo: nominativo,
      })
      setShowFirma(false)
      await loadScheda()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Errore nella firma')
    } finally {
      setFirmaLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link non accessibile</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!scheda) return null

  const isCompleted = scheda.stato === 'compilata' || scheda.stato === 'firmata'

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="text-xs text-gray-400 mb-1">{scheda.tenant_name}</div>
          <h1 className="text-xl font-bold text-gray-900">{scheda.template_nome}</h1>
          {scheda.template_descrizione && (
            <p className="text-sm text-gray-500 mt-1">{scheda.template_descrizione}</p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{scheda.codice}</span>
            {scheda.entity_label && (
              <span className="text-sm text-gray-600">{scheda.entity_label}</span>
            )}
            {isCompleted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                <CheckCircle className="w-3 h-3" />
                {scheda.stato === 'firmata' ? 'Firmata' : 'Compilata'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {isCompleted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-green-800 font-medium">
              Questa scheda e' stata {scheda.stato === 'firmata' ? 'firmata' : 'compilata'} con successo.
            </p>
            <p className="text-green-600 text-sm mt-1">Grazie per la compilazione!</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm p-6">
          <FormRenderer
            schema={scheda.schema}
            dati={dati}
            onChange={(field, value) => setDati(prev => ({ ...prev, [field]: value }))}
            readOnly={scheda.is_readonly}
            errors={{}}
          />
        </div>

        {/* Footer con azioni */}
        {!scheda.is_readonly && (
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saved ? 'Salvato!' : 'Salva'}
            </button>
            <button
              onClick={() => setShowFirma(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PenTool className="w-4 h-4" />
              Firma e Invia
            </button>
          </div>
        )}
      </div>

      {/* Firma Modal */}
      {showFirma && (
        <FirmaSempliceModal
          onClose={() => setShowFirma(false)}
          onConfirm={handleFirma}
          loading={firmaLoading}
        />
      )}
    </div>
  )
}
